import React, { useEffect, useState } from 'react';
import { appointmentsAPI } from '../services/api';
import { Appointment } from '../types';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { publicAssetPath } from '../utils/publicAssetPath';

const PublicAppointmentSearch: React.FC = () => {
  const [searchMode, setSearchMode] = useState<'national_id' | 'tracking_code'>('national_id');
  const [nationalId, setNationalId] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryValue = (searchMode === 'national_id' ? nationalId : trackingCode).trim();

  useEffect(() => {
    setAppointments([]);
    setError(null);
  }, [searchMode]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!queryValue) return;

    setLoading(true);
    setError(null);

    try {
      if (searchMode === 'national_id') {
        const response = await appointmentsAPI.getByNationalId(queryValue);
        setAppointments(response.data);
      } else {
        const response = await appointmentsAPI.getByTrackingCode(queryValue);
        const result = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
        setAppointments(result);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'خطا در جستجو');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };



  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'در انتظار';
      case 'in_progress': return 'در حال انجام';
      case 'completed': return 'تکمیل شده';
      case 'cancelled': return 'لغو شده';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header/Logo Area */}
        <div className="text-center mb-8 flex flex-col items-center">
          <img src={publicAssetPath('/logo.png')} alt="لوگو پایتخت" className="w-20 h-20 object-contain mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">پیگیری نوبت</h1>
          <p className="mt-2 text-sm text-gray-600">
            برای مشاهده وضعیت نوبت، اطلاعات زیر را وارد کنید
          </p>
        </div>

        {/* Search Card */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setSearchMode('national_id')}
              className={`flex-1 py-4 text-sm font-medium transition-colors relative ${searchMode === 'national_id'
                ? 'text-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              کد ملی
              {searchMode === 'national_id' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
            <button
              onClick={() => setSearchMode('tracking_code')}
              className={`flex-1 py-4 text-sm font-medium transition-colors relative ${searchMode === 'tracking_code'
                ? 'text-blue-600 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              کد رهگیری
              {searchMode === 'tracking_code' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
          </div>

          <div className="p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {searchMode === 'national_id' ? 'کد ملی' : 'شماره پیگیری'}
                </label>
                <input
                  type="text"
                  dir="ltr"
                  value={searchMode === 'national_id' ? nationalId : trackingCode}
                  onChange={(e) => searchMode === 'national_id' ? setNationalId(e.target.value) : setTrackingCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-wider placeholder-gray-400"
                  placeholder={searchMode === 'national_id' ? 'وارد کنید' : 'وارد کنید'}
                  maxLength={searchMode === 'national_id' ? 10 : 32}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !queryValue}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    در حال جستجو...
                  </span>
                ) : 'جستجو'}
              </button>
            </form>
          </div>

          {/* Results Area */}
          {(error || appointments.length > 0 || (queryValue && !loading && appointments.length === 0)) && (
            <div className="border-t border-gray-100 bg-gray-50/50 p-4 max-h-[400px] overflow-y-auto">
              {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 flex items-start gap-3">
                  <svg className="h-5 w-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}

              {appointments.length > 0 && (
                <div className="space-y-3">
                  {appointments.map((app) => (
                    <div key={app.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-900">نوبت #{app.id}</span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {getStatusText(app.status)}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>خدمت:</span>
                          <span className="text-gray-900">{app.service?.name || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>تاریخ ثبت:</span>
                          <span className="dir-ltr">{new Date(app.created_at).toLocaleDateString('fa-IR')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {queryValue && !loading && !error && appointments.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>نوبتی یافت نشد</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer/Back Action */}
        <div className="mt-8 text-center">
          <a href="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            بازگشت به صفحه اصلی
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicAppointmentSearch;
